/**
 * Template live preview — a full-tab look at one appearance template (spec 50 R8).
 *
 * Opened in a new tab from the gallery's "Live preview" link. The gallery card's
 * miniature answers "which one is which"; this answers "what would my store
 * actually look like" — the real storefront components at full size, themed,
 * greeting the merchant's own store by name, filled with the shared dummy
 * catalogue. The caption declares the data illustrative.
 *
 * Renders through `ComposerPreview`, so this can never drift from what the
 * editor canvas (and therefore the storefront) shows.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Monitor, Smartphone, Tablet } from "lucide-react";
import { Alert, Skeleton, toast } from "@flint/ui";
import { findAppearanceTemplate } from "@flint/api-contract/store-appearance-templates";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { WorkspaceShell } from "../../workspace/index.ts";
import { storeErrorMessage } from "../error-message.ts";
import {
  ComposerPreview,
  SAMPLE_COLLECTIONS,
  type PreviewDevice,
} from "./composer-preview.tsx";

const DEVICES: Array<{ id: PreviewDevice; label: string; Icon: typeof Monitor }> = [
  { id: "desktop", label: "Desktop", Icon: Monitor },
  { id: "tablet", label: "Tablet", Icon: Tablet },
  { id: "mobile", label: "Mobile", Icon: Smartphone },
];

export function TemplatePreviewPage({ templateKey }: { templateKey: string }) {
  const navigate = useNavigate();
  const canManage = usePermission("store.catalog.manage");
  const [device, setDevice] = useState<PreviewDevice>("desktop");

  const template = findAppearanceTemplate(templateKey);

  // Real store identity so the hero greets *their* store — the difference
  // between judging a template and judging someone else's storefront.
  const list = trpc.storeAppearances.list.useQuery({});
  const storeName = list.data?.storeName ?? "Your store";
  const storeSlug = list.data?.storeSlug ?? "your-store";

  // Built once per identity, not per render: the builders mint fresh section
  // ids on every call, and re-running them would churn every React key.
  const sections = useMemo(
    () =>
      template?.buildSections({
        storeName,
        tagline: null,
        coverImageUrl: null,
        // A dummy number so phone-dependent pieces (WhatsApp CTAs, the contact
        // button) preview at their fullest. Illustrative, like the products.
        phone: "9876543210",
      }) ?? [],
    [template, storeName],
  );

  const create = trpc.storeAppearances.create.useMutation({
    onSuccess: (res) => {
      toast.success(`"${res.name}" created`, {
        description: res.activated
          ? "It's live on your storefront — customise it now."
          : "Saved as a draft. Activate it when you're ready.",
      });
      void navigate({
        to: "/store/admin/storefront/$appearanceId",
        params: { appearanceId: res.appearanceId },
      });
    },
    onError: (err) =>
      toast.error("Couldn't create that appearance", { description: storeErrorMessage(err) }),
  });

  if (!template) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Template preview" }]} hideSectionTabs>
        <div className="mx-auto max-w-lg py-16">
          <Alert intent="bad" title="That template isn't available">
            It may have been renamed or retired. Pick one from the appearance library instead.
          </Alert>
          <Link
            to="/store/admin/storefront"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
          >
            <ArrowLeft size={14} aria-hidden />
            Back to Appearance
          </Link>
        </div>
      </WorkspaceShell>
    );
  }

  return (
    <WorkspaceShell
      breadcrumbs={[{ label: "Template preview" }]}
      hideTopbar
      hideSectionTabs
      disableViewportScroll
      contentClassName="mx-0 flex h-full w-full max-w-none"
    >
      <div className="flex h-full min-h-0 w-full flex-col">
        {/* ── Toolbar — same dark chrome as the editor ─────────── */}
        <div className="flex h-14 shrink-0 flex-wrap items-center gap-3 bg-[#1b2130] px-4 text-slate-100">
          <Link
            to="/store/admin/storefront"
            aria-label="Back to appearances"
            className="-ml-1 rounded-sm p-1 text-slate-300 transition hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft size={18} aria-hidden />
          </Link>
          <div className="min-w-0">
            <span className="block truncate text-sm font-semibold text-white">{template.name}</span>
            <span className="hidden truncate text-xs text-slate-400 sm:block">
              {template.description}
            </span>
          </div>

          <div
            className="mx-auto flex gap-1 rounded-lg bg-black/25 p-1"
            role="group"
            aria-label="Preview device"
          >
            {DEVICES.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setDevice(id)}
                aria-pressed={device === id}
                title={label}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  device === id ? "bg-white/15 text-white" : "text-slate-400 hover:text-slate-100"
                }`}
              >
                <Icon size={14} aria-hidden />
                <span className="hidden md:inline">{label}</span>
              </button>
            ))}
          </div>

          {canManage ? (
            <button
              type="button"
              disabled={create.isPending}
              onClick={() => create.mutate({ templateKey: template.key })}
              className="rounded-md bg-sky-600 px-3.5 py-1.5 text-sm font-semibold text-white transition hover:bg-sky-500 disabled:opacity-40"
            >
              {create.isPending ? "Creating…" : "Use this template"}
            </button>
          ) : (
            <span className="text-xs text-slate-400">View only</span>
          )}
        </div>

        {/* ── Canvas ───────────────────────────────────────────── */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[#eceef2] p-2.5">
          {list.isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <ComposerPreview
              sections={sections}
              theme={template.theme}
              device={device}
              storeName={storeName}
              storeSlug={storeSlug}
              collections={SAMPLE_COLLECTIONS}
              phone={"9876543210"}
              profile={{
                establishedYear: null,
                teamSize: null,
                monthlyCapacity: null,
                city: null,
                about: null,
              }}
            />
          )}
        </main>
      </div>
    </WorkspaceShell>
  );
}
